import {
  BOIType,
  IEntityOwnerFields,
  IHttpsCallableFailResponse,
  IHttpsCallableSuccessResponse,
  IOwner,
  IPersonOwnerFields,
  Modify,
  PaginatedData,
  SortDirection,
  UUID
} from "../../../src/app/core/interfaces";
import { FieldValue, Timestamp } from "firebase-admin/firestore";

interface GetAllOwnersSuccessRes extends IHttpsCallableSuccessResponse {
  owners: IOwnerDTO[];
}
export type GetAllOwnersRes = GetAllOwnersSuccessRes | IHttpsCallableFailResponse;

export interface GetOwnerReq {
  ownerId: UUID;
  isNewlyCreated: boolean;
}

interface GetOwnerSuccessRes extends IHttpsCallableSuccessResponse {
  owner: IOwnerDTO;
}
export type GetOwnerRes = GetOwnerSuccessRes | IHttpsCallableFailResponse;

export interface SearchOwnersReq {
  owner: IOwner
}

interface SearchOwnersSuccessRes extends IHttpsCallableSuccessResponse {
  owners: IOwnerDTO[];
}
export type SearchOwnersRes = SearchOwnersSuccessRes | IHttpsCallableFailResponse;

export interface GetOwnersByEntityIdReq {
  entityId: UUID;
  orgId?: UUID;
}

interface GetOwnersByEntityIdSuccessRes extends IHttpsCallableSuccessResponse {
  owners: IOwnerDTO[]
}
export type GetOwnersByEntityIdRes = GetOwnersByEntityIdSuccessRes | IHttpsCallableFailResponse;

export interface RemoveEntityFromOwnerReq {
  ownerId: UUID,
  entityId: UUID,
  orgId: UUID | null,
}

interface RemoveEntityFromOwnerSuccessRes extends IHttpsCallableSuccessResponse {
  updatedOwner: IOwnerDTO | null
}
export type RemoveEntityFromOwnerRes = RemoveEntityFromOwnerSuccessRes | IHttpsCallableFailResponse;

export interface RemoveOrgEntitiesFromOwnerReq {
  ownerId: UUID,
  orgId: UUID,
}

interface RemoveOrgEntitiesFromOwnerSuccessRes extends IHttpsCallableSuccessResponse {
  updatedOwner: IOwnerDTO | null
}
export type RemoveOrgEntitiesFromOwnerRes = RemoveOrgEntitiesFromOwnerSuccessRes | IHttpsCallableFailResponse;

interface UploadGovIdImageBaseReq {
  image: string;
  mediaType: string;
}

export interface UploadGovIdImageByTokenReq extends UploadGovIdImageBaseReq {
  token: string;
}

export interface UploadGovIdImageByOwnerIdReq extends UploadGovIdImageBaseReq {
  ownerId: UUID;
}

export type UploadGovIdImageReq = UploadGovIdImageByTokenReq | UploadGovIdImageByOwnerIdReq;

interface UploadGovIdImageSuccessRes extends IHttpsCallableSuccessResponse {
  message: "Image uploaded."
}

export type UploadGovIdImageRes = UploadGovIdImageSuccessRes | IHttpsCallableFailResponse;

export type IOwnerDTO  = Modify<IOwner, {
  lastModifiedDate: Timestamp | null;
}>;

export interface IOwnerCf extends Omit<IOwnerDTO, "entityIds" | "groupIds">{
  entityIdsMap: {[orgId in UUID | "secureFile"]?: UUID[]};
  groupIdsMap: {[orgId in UUID]?: UUID[]};
  /**
   * Stores the ID of the last user who made changes to the document,
   * or <u>"auto-collect"</u> if the changes were made during the automatic collection of information about the owner.
   */
  lastModifiedBy: UUID | "auto-collect";
}

export type IOwnerCfWithBoiType = IOwnerCf & { boiType: BOIType };
export type IOwnerCfTuple = [IOwnerCf, BOIType[]];

export type IPersonOwnerFieldsCf = Modify<IPersonOwnerFields, {
  lastIdCollectionEmailSent: Timestamp | null;
  dob: Timestamp | null;
}>;

export type IPersonOwnerDTO = IOwnerDTO & IPersonOwnerFieldsCf;

export type IPersonOwnerCf = IOwnerCf & IPersonOwnerFieldsCf;

export const isPersonOwnerDTO = (owner: IOwnerDTO): owner is IPersonOwnerDTO => {
  return !owner.isEntity;
}

export const isPersonOwnerCf = (owner: IOwnerCf): owner is IPersonOwnerCf => {
  return !owner.isEntity;
}

export type IEntityOwnerDTO = IOwnerDTO & IEntityOwnerFields;

export type IEntityOwnerCf = IOwnerCf & IEntityOwnerFields;

export const isEntityOwnerDTO = (owner: IOwnerDTO): owner is IEntityOwnerDTO => {
  return owner.isEntity;
}

export const isEntityOwnerCf = (owner: IOwnerCf): owner is IEntityOwnerCf => {
  return owner.isEntity;
}

export type CombinedOwnerFields = "displayName" | "idSaved"
export interface PaginatedDataReq<T, AdditionalFields extends string = never> {
  sort: {
    field: keyof T | AdditionalFields;
    direction: SortDirection;
  },
  paginator: {
    pageNumber: number;
    pageSize: number;
  }
  search: string;
  filters: { [field in keyof T | AdditionalFields]?: string | boolean };
}

interface GetOwnersPaginatedSuccessRes<T extends IOwnerDTO> extends IHttpsCallableSuccessResponse {
  data: PaginatedData<T>
}
export type GetOwnersPaginatedRes<T extends IOwnerDTO> =
  GetOwnersPaginatedSuccessRes<T> | IHttpsCallableFailResponse;

export type FieldDeletionMap<T> = { [K in keyof T]: FieldValue };
